import { Row, Col, Typography, Button, Grid, Collapse, theme } from "antd";
import { FeatureCard } from "@mealmanager/homepage-ui";
import { publicFaqItems } from "@mealmanager/faq";
import heroMealPic from '../../../assets/homepage-hero-meal_2.webp';
import recipesPic from '../../../assets/homepage-recipes.webp';
import sousChefPic from '../../../assets/homepage-sous-chef-v2.webp';
import mealPlanPic from '../../../assets/homepage-meal-plan.webp';
import shoppingListPic from '../../../assets/homepage-shopping-list.webp';
import { useContext, useEffect } from "react";
import { ConfigContext } from "src/app/components/Config";
import type { ConfigValues } from "src/app/components/Config";
import type { BlogPostSummary } from "../blog/blog.server";
import { PostCard } from "../blog/BlogListingPage";
import { Link } from "react-router";
import PagePadding from "~/components/PagePadding";
import { sageCreamPalette } from "../components/AppShell/Theme";

const { Title, Paragraph } = Typography;

type PageProps = {
    initialConfig?: ConfigValues;
    posts?: BlogPostSummary[];
    showBlogLink?: boolean;
};

const storyParagraphs = [
    "Like many people, we realised we'd fallen into the habit of eating the same meals week after week. Life was busy, convenience came first, and we realised more and more of our meals relied on processed foods.",
    "Then we started cooking more from scratch again. We wanted to eat more real foods, cut our carbs and get more vegetables onto our plates. It sounded simple — but finding recipes that balanced everything, planning meals and understanding our nutrition quickly became overwhelming.",
    "We couldn't find an app that brought it all together. So we built the app we wished we'd had. We don't believe in banning foods or chasing perfection. We believe that understanding what's on your plate helps you make better choices and build healthier habits over time.",
    "Real food. Made simple.",
];

export default function Page({ initialConfig, posts = [], showBlogLink = false }: PageProps = {}) {
    const { token } = theme.useToken();
    const { state: contextConfig, setState } = useContext(ConfigContext);
    const config = contextConfig ?? initialConfig;
    const goToAuthed = (path: string) => window.location.href = config?.appBaseUrl + path;
    useEffect(() => {
        if (!contextConfig && initialConfig) {
            setState(initialConfig);
        }
    }, [contextConfig, initialConfig, setState]);
    const screens = Grid.useBreakpoint();
    const isLg = !!screens.lg;
    const linkStyle = { textDecoration: "none" as const };

    return (
        <>
            <PagePadding style={{ gap: isLg ? 32 : 20 }}>
                <section style={{ textAlign: "center", padding: isLg ? "24px 0 0" : "8px 0 0" }}>
                    <span
                        style={{
                            display: "inline-block",
                            background: sageCreamPalette.sageSoft,
                            color: sageCreamPalette.sageDark,
                            fontSize: isLg ? 15 : 13,
                            fontWeight: 600,
                            padding: isLg ? "8px 18px" : "6px 14px",
                            borderRadius: 999,
                            marginBottom: isLg ? 24 : 16,
                        }}
                    >
                        Less factory, more farm
                    </span>
                    <Title
                        className="display-heading"
                        level={1}
                        style={{ margin: "0 0 20px", fontSize: isLg ? 68 : 40, lineHeight: 1.12, color: sageCreamPalette.text }}
                    >
                        Real food, made simple.
                    </Title>
                    <Paragraph
                        style={{
                            fontSize: isLg ? 20 : 16,
                            lineHeight: 1.6,
                            color: sageCreamPalette.textMuted,
                            maxWidth: 640,
                            width: isLg ? undefined : "calc(100% - 16px)",
                            margin: "0 auto 32px",
                        }}
                    >
                        Discover delicious whole-food recipes, personalised meal plans, and meaningful nutrition in one place — all tailored to the way you eat.
                    </Paragraph>
                    <Row gutter={[isLg ? 20 : 14, 14]} style={{ marginBottom: isLg ? 8 : 0 }}>
                        <Col xs={24} style={{ minWidth: 0 }}>
                            <img
                                src={heroMealPic}
                                alt="A colourful whole-food meal ready to share"
                                width={1600}
                                height={1000}
                                loading="eager"
                                decoding="async"
                                fetchPriority="high"
                                style={{
                                    display: "block",
                                    width: "100%",
                                    height: "clamp(220px, 30vw, 380px)",
                                    borderRadius: 24,
                                    objectFit: "cover",
                                }}
                            />
                        </Col>
                    </Row>
                    <div style={{ display: "flex", justifyContent: "center", marginTop: isLg ? 28 : 20 }}>
                        <Button type="primary" size="large" onClick={() => goToAuthed("/auth/sign-in")}>
                            Get started for free
                        </Button>
                    </div>
                </section>
            </PagePadding>

            <PagePadding style={{ gap: isLg ? 20 : 14 }}>
                <section style={{ maxWidth: 820, margin: isLg ? "0" : "0 8px", padding: isLg ? "40px 0 20px" : "24px 0 8px" }}>
                    <Title className="display-heading" level={2} style={{ fontSize: isLg ? 44 : 28, margin: "0 0 16px", color: sageCreamPalette.text }}>
                        One app, every step.
                    </Title>
                    <Paragraph style={{ color: sageCreamPalette.textMuted, fontSize: isLg ? 18 : 15, lineHeight: 1.65 }}>
                        Nutrition is evolving, but healthy eating has become more confusing than ever. Ultra-processed shortcuts are everywhere, advice changes constantly, and reaching your health goals often means juggling multiple apps.
                    </Paragraph>
                    <Paragraph style={{ color: sageCreamPalette.textMuted, fontSize: isLg ? 18 : 15, lineHeight: 1.65, marginBottom: 0 }}>
                        Realvore brings everything together. Whether you&apos;re eating low carb, chasing protein goals or simply trying to get more vegetables onto your plate, Realvore combines real food, modern nutrition and AI to make home cooking simpler.
                    </Paragraph>
                </section>
            </PagePadding>

            <PagePadding style={{ gap: isLg ? 24 : 16 }}>
                <section style={{ padding: isLg ? "48px 0 0" : "24px 0 0" }}>
                    <Title className="display-heading" level={2} style={{ fontSize: isLg ? 44 : 28, margin: "0 0 10px" }}>
                        How it works
                    </Title>
                </section>

                <section style={{ display: "flex", flexDirection: "column", gap: isLg ? 24 : 16 }}>
                    <Row gutter={[isLg ? 24 : 16, isLg ? 24 : 16]}>
                        <Col xs={24} md={12}>
                            <FeatureCard
                                title="Recipes you'll actually want to cook"
                                description="Browse delicious recipes made with real ingredients inspired by cuisines from around the world, from comforting classics to bold new flavours. Whether you're eating high protein, keto, vegan, vegetarian or simply looking to eat more real food, there's something for everyone. Search for recipes using powerful filters to help you find exactly what you're looking for. Save your favourites, rate recipes and discover new additions every week."
                                image={{ src: recipesPic, alt: "A selection of colourful whole-food recipes" }}
                                imageHeight={isLg ? 240 : 150}
                                titleFontSize={isLg ? 27 : 19}
                                titleColor={sageCreamPalette.sageDark}
                                descriptionColor={sageCreamPalette.textMuted}
                                cardBackground={sageCreamPalette.cardBg}
                                action={config?.recipeLinkEnabled ? (
                                    <Link to="/recipes" style={linkStyle}>
                                        <Button type="primary">Explore recipes</Button>
                                    </Link>
                                ) : null}
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <FeatureCard
                                title="Your AI Sous Chef"
                                description="Need inspiration? Want to make a family favourite lower carb? Cooking with what's already in the fridge? Just tell Sous Chef what you fancy, and it does the rest — from step-by-step instructions and ingredient lists to complete nutrition profiles. Tell Sous Chef a little about your household, and every recipe it creates is automatically tailored to the way you eat."
                                image={{ src: sousChefPic, alt: "A home cook viewing the Sous Chef app while preparing dinner" }}
                                imageHeight={isLg ? 240 : 150}
                                titleFontSize={isLg ? 27 : 19}
                                titleColor={sageCreamPalette.sageDark}
                                descriptionColor={sageCreamPalette.textMuted}
                                cardBackground={sageCreamPalette.cardBg}
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <FeatureCard
                                title="Meal planning minus the maths"
                                description="Create personalised meal plans in minutes and see your nutrition at a glance. Go beyond calories and track macros, plant diversity, veg portions, and how much of your plate comes from real food, giving you a clearer picture of your nutrition. No spreadsheets, no switching between apps."
                                image={{ src: mealPlanPic, alt: "A varied week of planned whole-food meals" }}
                                imageHeight={isLg ? 240 : 150}
                                titleFontSize={isLg ? 27 : 19}
                                titleColor={sageCreamPalette.sageDark}
                                descriptionColor={sageCreamPalette.textMuted}
                                cardBackground={sageCreamPalette.cardBg}
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <FeatureCard
                                title="Shopping lists in one click"
                                description="Turn your meal plan or favourite recipes into a shopping list with one click, tick off ingredients as you shop and add household essentials along the way."
                                image={{ src: shoppingListPic, alt: "A shopper using a digital list to choose fresh produce" }}
                                imageHeight={isLg ? 240 : 150}
                                titleFontSize={isLg ? 27 : 19}
                                titleColor={sageCreamPalette.sageDark}
                                descriptionColor={sageCreamPalette.textMuted}
                                cardBackground={sageCreamPalette.cardBg}
                            />
                        </Col>
                    </Row>
                    <Title className="display-heading" level={2} style={{ fontSize: isLg ? 34 : 22, margin: isLg ? "8px 0 0" : "4px 0 0", color: sageCreamPalette.sageDark }}>
                        Dinner, sorted.
                    </Title>
                </section>
            </PagePadding>

            {posts.length > 0 && (
                <PagePadding style={{ gap: isLg ? 24 : 16 }}>
                    <section aria-labelledby="latest-posts-heading" style={{ padding: isLg ? "48px 0" : "28px 0" }}>
                        <Title id="latest-posts-heading" className="display-heading" level={2}
                            style={{ fontSize: isLg ? 44 : 28, margin: "0 0 20px", color: sageCreamPalette.text }}>
                            Latest from the blog
                        </Title>
                        <Row gutter={[isLg ? 24 : 16, isLg ? 24 : 16]}>
                            {posts.map((post) => (
                                <Col key={post._id} xs={24} md={posts.length === 1 ? 12 : 8}>
                                    <PostCard post={post} />
                                </Col>
                            ))}
                        </Row>
                    </section>
                </PagePadding>
            )}

            {posts.length === 0 && showBlogLink && (
                <PagePadding style={{ gap: isLg ? 24 : 16 }}>
                    <section aria-labelledby="blog-teaser-heading" style={{ padding: isLg ? "48px 0" : "28px 0" }}>
                        <Title id="blog-teaser-heading" className="display-heading" level={2}
                            style={{ fontSize: isLg ? 44 : 28, margin: "0 0 12px", color: sageCreamPalette.text }}>
                            Read our blog
                        </Title>
                        <Paragraph style={{ margin: "0 0 16px", color: sageCreamPalette.text }}>
                            Recipes, nutrition and ideas for eating more real food.
                        </Paragraph>
                        <Link to="/blog" style={{ fontWeight: 600, color: sageCreamPalette.sageDark }}>Visit the blog →</Link>
                    </section>
                </PagePadding>
            )}

            <section style={{ background: sageCreamPalette.sageDark, padding: isLg ? "72px 48px 80px" : "40px 20px 44px" }}>
                <div style={{ maxWidth: 1180, margin: "0 auto" }}>
                    <Title className="display-heading" level={3} style={{ fontSize: isLg ? 34 : 22, margin: "0 0 20px", color: sageCreamPalette.pageBg }}>
                        Our story
                    </Title>
                    {storyParagraphs.map((paragraph, i) => (
                        <Paragraph
                            key={paragraph}
                            style={{
                                fontSize: isLg ? 17 : 13.5,
                                lineHeight: 1.7,
                                maxWidth: 760,
                                color: sageCreamPalette.sageSoft,
                                marginBottom: i === storyParagraphs.length - 1 ? 0 : 16,
                            }}
                        >
                            {paragraph}
                        </Paragraph>
                    ))}
                </div>
            </section>

            <section style={{ background: sageCreamPalette.sageSoft, padding: isLg ? "64px 48px" : "40px 20px" }}>
                <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
                    <Title className="display-heading" level={2} style={{ color: sageCreamPalette.sageDark, fontSize: isLg ? 40 : 28, margin: "0 0 12px" }}>
                        Ready to eat more real food?
                    </Title>
                    <Paragraph style={{ color: sageCreamPalette.textMuted, fontSize: isLg ? 18 : 15, lineHeight: 1.6, margin: "0 auto 24px" }}>
                        Join Realvore today and discover a simpler way to cook, plan and eat well.
                    </Paragraph>
                    <Button type="primary" size="large" onClick={() => goToAuthed("/auth/sign-in")}>
                        Get started for free
                    </Button>
                </div>
            </section>

            <PagePadding style={{ gap: isLg ? 20 : 14 }}>
                <section id="faq" style={{ padding: isLg ? "48px 0" : "24px 0", scrollMarginTop: 80 }}>
                    <Title className="display-heading" level={2} style={{ fontSize: isLg ? 44 : 28, margin: "0 0 20px", textAlign: "center" }}>
                        Frequently asked questions
                    </Title>
                    <div
                        style={{
                            maxWidth: 820,
                            margin: "0 auto",
                            background: token.colorBgContainer,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: token.borderRadiusLG,
                            padding: isLg ? 20 : 12,
                        }}
                    >
                        <Collapse
                            bordered={false}
                            expandIconPlacement="end"
                            items={publicFaqItems.map((item) => ({
                                key: item.id,
                                label: <span style={{ fontWeight: 600 }}>{item.question}</span>,
                                children: item.answer.map((paragraph) => (
                                    <Paragraph key={paragraph} type="secondary" style={{ lineHeight: 1.65, marginBottom: 12 }}>
                                        {paragraph}
                                    </Paragraph>
                                )),
                            }))}
                            style={{ background: "transparent" }}
                        />
                    </div>
                </section>
            </PagePadding>
        </>
    );
}
